import * as React from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { ArrowDown, ArrowLeft, ArrowUp, Pencil, Trash2 } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { QuestionForm } from "@/components/quizzes/QuestionForm"
import { QuestionList } from "@/components/quizzes/QuestionList"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { ContentAreaGroup } from "@/types/flashcards"
import type { CaseNarrative, CaseQuestion, Section } from "@/types/exams"

type CaseDetail = CaseNarrative & {
  ai_draft: boolean
  drafting_status: string | null
  drafting_error: string | null
  position: number
}

export default function AdminCaseStudyShow({
  exam,
  case_study,
  questions,
  sections,
  content_area_groups,
}: {
  exam: { id: number; title: string; status: string }
  case_study: CaseDetail
  questions: CaseQuestion[]
  sections: { value: Section; label: string }[]
  content_area_groups: ContentAreaGroup[]
}) {
  const [editing, setEditing] = React.useState<CaseQuestion | null>(null)
  const [deleting, setDeleting] = React.useState<CaseQuestion | null>(null)
  const [deletingCase, setDeletingCase] = React.useState(false)
  const basePath = `/admin/case_studies/${case_study.id}`
  const title = `${case_study.title} (case ${case_study.position})`
  const description = `Edit the narrative and questions for case ${case_study.position} of ${exam.title}.`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <Link href={`/admin/practice_exams/${exam.id}`} className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {exam.title}
        </Link>
        <PageHeader
          title={case_study.title}
          description={
            <>
              {case_study.ai_draft && (
                <Badge tone="signal" className="mr-2">
                  AI draft – needs review
                </Badge>
              )}
              Case {case_study.position} · {questions.length} {questions.length === 1 ? "question" : "questions"}
              {case_study.presenting_concern ? ` · ${case_study.presenting_concern}` : ""}
            </>
          }
          actions={
            <>
              <Button type="button" variant="ghost" size="icon" aria-label="Delete case" onClick={() => setDeletingCase(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
              {case_study.ai_draft && (
                <Button type="button" onClick={() => router.patch(`${basePath}/mark_reviewed`)}>
                  Mark reviewed
                </Button>
              )}
            </>
          }
        />

        <NarrativeForm caseStudy={case_study} action={basePath} />

        {sections.map((section) => {
          const inSection = questions.filter((q) => q.section === section.value)
          return (
            <section key={section.value} className="mt-10">
              <h2>Questions after {section.label.toLowerCase()}</h2>
              {inSection.length === 0 ? (
                <p className="mt-2">No questions follow this section yet.</p>
              ) : (
                <QuestionList
                  questions={inSection}
                  actions={(q) => {
                    const i = inSection.findIndex((x) => x.id === q.id)
                    const move = (direction: "up" | "down") =>
                      router.patch(`${basePath}/questions/${q.id}/move`, { direction }, { preserveScroll: true })
                    return (
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                        <Button type="button" variant="ghost" size="icon" aria-label="Move question up" disabled={i === 0} onClick={() => move("up")}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" aria-label="Move question down" disabled={i === inSection.length - 1} onClick={() => move("down")}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" aria-label="Edit question" onClick={() => setEditing(q as CaseQuestion)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" aria-label="Delete question" onClick={() => setDeleting(q as CaseQuestion)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  }}
                />
              )}
            </section>
          )
        })}

        <section className="mt-10">
          <h2>Add a question</h2>
          <div className="callout mt-4">
            <QuestionForm
              idPrefix="new-question"
              action={`${basePath}/questions`}
              method="post"
              contentAreaGroups={content_area_groups}
              requireExplanation
              submitLabel="Add question"
              sections={sections}
            />
          </div>
        </section>

        <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
          <DialogContent size="lg">
            <DialogHeader>
              <DialogTitle>Edit question</DialogTitle>
            </DialogHeader>
            {editing && (
              <div className="mt-4">
                <QuestionForm
                  key={editing.id}
                  idPrefix="edit-question"
                  action={`${basePath}/questions/${editing.id}`}
                  method="patch"
                  question={editing}
                  contentAreaGroups={content_area_groups}
                  requireExplanation
                  submitLabel="Save question"
                  sections={sections}
                  onSuccess={() => setEditing(null)}
                  onCancel={() => setEditing(null)}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
        <ConfirmDialog
          open={!!deleting}
          onOpenChange={(open) => !open && setDeleting(null)}
          title="Delete this question?"
          description="Scores from finished attempts stay the same. This can't be undone."
          confirmLabel="Delete question"
          onConfirm={() => deleting && router.delete(`${basePath}/questions/${deleting.id}`, { preserveScroll: true, onFinish: () => setDeleting(null) })}
        />
        <ConfirmDialog
          open={deletingCase}
          onOpenChange={setDeletingCase}
          title="Delete this case?"
          description={`"${case_study.title}" and its ${questions.length} questions will be removed from the exam. Finished scores stay the same.`}
          confirmLabel="Delete case"
          onConfirm={() => router.delete(basePath)}
        />
      </AdminShell>
    </>
  )
}

function NarrativeForm({ caseStudy, action }: { caseStudy: CaseDetail; action: string }) {
  const form = useForm({
    title: caseStudy.title,
    presenting_concern: caseStudy.presenting_concern ?? "",
    intake_summary: caseStudy.intake_summary ?? "",
    session_1: caseStudy.session_1 ?? "",
    session_2: caseStudy.session_2 ?? "",
  })
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.patch(action, { preserveScroll: true })
  }
  const field = (key: "intake_summary" | "session_1" | "session_2", label: string) => (
    <div className="space-y-2">
      <label htmlFor={`case-${key}`}>{label}</label>
      <textarea
        id={`case-${key}`}
        className="form-control form-control-textarea min-h-40"
        value={form.data[key]}
        onChange={(e) => form.setData(key, e.target.value)}
      />
    </div>
  )

  return (
    <section className="mt-8">
      <h2>Case narrative</h2>
      <form onSubmit={submit} className="callout mt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="case-title">Title</label>
            <Input id="case-title" required value={form.data.title} onChange={(e) => form.setData("title", e.target.value)} />
            {form.errors.title && <p className="text-xs text-danger-display">{form.errors.title}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor="case-concern">Presenting concern</label>
            <Input
              id="case-concern"
              value={form.data.presenting_concern}
              onChange={(e) => form.setData("presenting_concern", e.target.value)}
            />
          </div>
        </div>
        {field("intake_summary", "Intake summary")}
        {field("session_1", "Session 1")}
        {field("session_2", "Session 2")}
        <Button type="submit" disabled={form.processing || !form.isDirty}>
          Save narrative
        </Button>
      </form>
    </section>
  )
}
